import { X, Sparkles, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { useEffect, useState } from 'react';

interface AISummaryModalProps {
  roomId: string;
  onClose: () => void;
  baseUrl: string;
}

type SummaryStatus = 'pending' | 'processing' | 'completed' | 'failed' | 'no_transcript' | 'no_api_key' | 'poor_transcript';

export function AISummaryModal({ roomId, onClose, baseUrl }: AISummaryModalProps) {
  const [status, setStatus] = useState<SummaryStatus>('pending');
  const [summary, setSummary] = useState<string[]>([]);
  const [transcript, setTranscript] = useState<string | null>(null);
  const [showTranscript, setShowTranscript] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let pollInterval: NodeJS.Timeout | null = null;

    const fetchSummary = async () => {
      try {
        const response = await fetch(`${baseUrl}/calls/${roomId}/ai-summary`);
        const data = await response.json();

        if (data.ok) {
          setStatus(data.status);
          setSummary(data.summary || []);
          setTranscript(data.transcript || null);

          // Stop polling if completed or failed
          if (data.status === 'completed' || data.status === 'failed' ||
              data.status === 'no_transcript' || data.status === 'no_api_key' ||
              data.status === 'poor_transcript') {
            if (pollInterval) {
              clearInterval(pollInterval);
              pollInterval = null;
            }
          }
        } else {
          setError(data.error || 'Failed to fetch AI summary');
        }
      } catch (err) {
        console.error('Error fetching AI summary:', err);
        setError('Failed to fetch AI summary');
      }
    };

    // Initial fetch
    fetchSummary();

    // Poll every 3 seconds while processing
    pollInterval = setInterval(() => {
      if (status === 'processing' || status === 'pending') {
        fetchSummary();
      }
    }, 3000);

    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, [roomId, baseUrl, status]);

  const getStatusIcon = () => {
    switch (status) {
      case 'processing':
      case 'pending':
        return <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />;
      case 'completed':
        return <CheckCircle className="w-6 h-6 text-green-500" />;
      case 'failed':
      case 'no_transcript':
      case 'no_api_key':
      case 'poor_transcript':
        return <AlertCircle className="w-6 h-6 text-yellow-500" />;
      default:
        return <Sparkles className="w-6 h-6 text-purple-500" />;
    }
  };

  const getStatusText = () => {
    switch (status) {
      case 'pending':
        return 'Starting AI analysis...';
      case 'processing':
        return 'Generating AI summary...';
      case 'completed':
        return 'AI Summary';
      case 'failed':
        // If we have a transcript, it's just summary generation that failed
        return transcript ? 'Summary Generation Failed' : 'Could Not Process Call';
      case 'no_transcript':
        return 'No Conversation Detected';
      case 'no_api_key':
        return 'AI Summary Unavailable';
      case 'poor_transcript':
        return 'Insufficient Content';
      default:
        return 'AI Summary';
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[200] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            {getStatusIcon()}
            <h2 className="text-2xl font-bold text-gray-900">{getStatusText()}</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          )}

          {/* Processing State */}
          {(status === 'pending' || status === 'processing') && (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-12 h-12 text-blue-500 animate-spin mb-4" />
              <p className="text-gray-600 text-lg mb-2">
                {status === 'pending' ? 'Analyzing call recording...' : 'Generating AI summary...'}
              </p>
              <p className="text-gray-400 text-sm">This may take a minute or two</p>
            </div>
          )}

          {/* Completed State */}
          {status === 'completed' && summary.length > 0 && (
            <div>
              <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-4 mb-4 border border-purple-200">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-5 h-5 text-purple-600" />
                  <h3 className="font-semibold text-purple-900">Call Summary</h3>
                </div>
                <p className="text-sm text-purple-700">
                  AI-generated summary of your conversation
                </p>
              </div>

              <ul className="space-y-3 mb-6">
                {summary.map((point, index) => (
                  <li key={index} className="flex gap-3">
                    <span className="flex-shrink-0 w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-medium">
                      {index + 1}
                    </span>
                    <p className="text-gray-700 flex-1 pt-0.5">{point}</p>
                  </li>
                ))}
              </ul>

              {/* Transcript Toggle */}
              {transcript && (
                <div className="border-t border-gray-200 pt-4">
                  <button
                    onClick={() => setShowTranscript(!showTranscript)}
                    className="text-blue-600 hover:text-blue-700 font-medium text-sm flex items-center gap-2"
                  >
                    {showTranscript ? 'Hide' : 'Show'} Full Transcript
                  </button>

                  {showTranscript && (
                    <div className="mt-4 bg-gray-50 rounded-lg p-4 max-h-64 overflow-y-auto">
                      <p className="text-gray-700 text-sm whitespace-pre-wrap">{transcript}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* No Transcript State */}
          {status === 'no_transcript' && (
            <div className="flex flex-col items-center justify-center py-12">
              <AlertCircle className="w-12 h-12 text-yellow-500 mb-4" />
              <p className="text-gray-700 text-lg mb-2">No Conversation Detected</p>
              <p className="text-gray-500 text-sm text-center max-w-md">
                No conversation was captured during this call. The call may have been too short or no one spoke.
              </p>
            </div>
          )}

          {/* No API Key State */}
          {status === 'no_api_key' && (
            <div className="flex flex-col items-center justify-center py-12">
              <AlertCircle className="w-12 h-12 text-yellow-500 mb-4" />
              <p className="text-gray-700 text-lg mb-2">AI Summary Unavailable</p>
              <p className="text-gray-500 text-sm text-center max-w-md">
                AI call summaries are temporarily unavailable. This feature will be available soon.
              </p>
            </div>
          )}

          {/* Poor Transcript State */}
          {status === 'poor_transcript' && (
            <div className="flex flex-col items-center justify-center py-12">
              <AlertCircle className="w-12 h-12 text-yellow-500 mb-4" />
              <p className="text-gray-700 text-lg mb-2">Insufficient Content for Summary</p>
              <p className="text-gray-500 text-sm text-center max-w-md">
                The conversation was too brief or unclear to generate a meaningful AI summary. Try having a longer conversation with more substantial discussion.
              </p>

              {/* Show transcript if available */}
              {transcript && (
                <div className="w-full mt-6 border-t border-gray-200 pt-4">
                  <h3 className="font-semibold text-gray-900 mb-2 text-center">Available Transcript</h3>
                  <div className="bg-gray-50 rounded-lg p-4 max-h-64 overflow-y-auto">
                    <p className="text-gray-700 text-sm whitespace-pre-wrap">{transcript}</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Failed State */}
          {status === 'failed' && (
            <div className="flex flex-col items-center justify-center py-12">
              <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
              <p className="text-gray-700 text-lg mb-2">
                {transcript ? 'Summary Generation Failed' : 'Could Not Process Call'}
              </p>
              <p className="text-gray-500 text-sm text-center max-w-md mb-4">
                {transcript
                  ? 'Unable to generate AI summary, but transcript is available below.'
                  : 'No conversation was detected during this call. The call may have been too short or no one spoke.'}
              </p>

              {/* Show transcript if available even if summary failed */}
              {transcript && (
                <div className="w-full mt-4 border-t border-gray-200 pt-4">
                  <h3 className="font-semibold text-gray-900 mb-2">Full Transcript</h3>
                  <div className="bg-gray-50 rounded-lg p-4 max-h-64 overflow-y-auto">
                    <p className="text-gray-700 text-sm whitespace-pre-wrap">{transcript}</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-4 bg-gray-50">
          <button
            onClick={onClose}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
